import { Injectable, ErrorHandler, inject, NgZone } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { NotificationService } from './notification.service';

/**
 * Service to automatically detect new application deployments and refresh the app
 * without requiring the user to manually clear browser or Android storage.
 */
@Injectable({
  providedIn: 'root'
})
export class AppUpdateService {
  private readonly notificationService = inject(NotificationService);
  private readonly ngZone = inject(NgZone);
  private checkInterval: any = null;
  private isUpdating = false;

  /** Installed app reads the uploaded site. Desktop keeps the page it is already on. */
  private liveOrigin(): string {
    if (Capacitor.isNativePlatform()) return 'https://parking.kormoan.in';
    return window.location.origin;
  }

  public start(): void {
    if (typeof window === 'undefined') return;

    // Check 6 seconds after launch
    setTimeout(() => {
      void this.checkForUpdate();
    }, 6000);

    // Periodic check every 15 minutes
    this.ngZone.runOutsideAngular(() => {
      this.checkInterval = setInterval(() => {
        void this.checkForUpdate();
      }, 15 * 60 * 1000);

      // Check whenever user switches back to the app tab/window
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            void this.checkForUpdate();
          }
        });
      }

      // Check when internet comes back online
      window.addEventListener('online', () => {
        void this.checkForUpdate();
      });
    });
  }

  private getRunningScriptHashes(): string[] {
    if (typeof document === 'undefined') return [];
    const scripts = Array.from(document.querySelectorAll('script[src]')) as HTMLScriptElement[];
    return scripts
      .map(s => {
        const src = s.getAttribute('src') || '';
        const filename = src.split('/').pop() || src;
        return filename.split('?')[0];
      })
      .filter(src => src && (src.includes('chunk-') || src.includes('main') || src.includes('polyfills')))
      .sort();
  }

  public async checkForUpdate(): Promise<boolean> {
    if (this.isUpdating || typeof window === 'undefined' || (typeof navigator !== 'undefined' && !navigator.onLine)) {
      return false;
    }

    try {
      const res = await fetch(`${this.liveOrigin()}/?_app_check=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (!res.ok) return false;
      const html = await res.text();

      // Extract script tags from remote index.html
      const matches = Array.from(html.matchAll(/<script[^>]+src=["']([^"']+)["']/gi)).map(m => m[1]);
      const remoteScripts = matches
        .map(src => {
          const filename = src.split('/').pop() || src;
          return filename.split('?')[0];
        })
        .filter(src => src && (src.includes('chunk-') || src.includes('main') || src.includes('polyfills')))
        .sort();

      if (remoteScripts.length === 0) return false;

      const currentScripts = this.getRunningScriptHashes();
      if (currentScripts.length === 0) return false;

      // Compare script names / hashes
      const isDifferent = remoteScripts.length !== currentScripts.length ||
        remoteScripts.some((src, i) => src !== currentScripts[i]);

      if (isDifferent) {
        console.log('[AutoUpdate] New version detected! Refreshing app...');
        await this.applyUpdate();
        return true;
      }
    } catch (_) {}

    return false;
  }

  public async applyUpdate(): Promise<void> {
    if (this.isUpdating) return;
    this.isUpdating = true;

    try {
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k)));
      }
    } catch (_) {}

    try {
      this.notificationService.showInfo('New update detected. Refreshing app...', 2500);
    } catch (_) {}

    setTimeout(() => {
      const url = Capacitor.isNativePlatform()
        ? new URL(this.liveOrigin())
        : new URL(window.location.href);
      url.searchParams.set('_v', Date.now().toString());
      window.location.replace(url.toString());
    }, 1200);
  }
}

/**
 * Intercepts ChunkLoadErrors that happen when a new deployment deletes old chunk files,
 * automatically reloading with cache bust instead of stranding the user on a blank screen.
 */
@Injectable({
  providedIn: 'root'
})
export class GlobalAppErrorHandler implements ErrorHandler {
  private static lastReloadTime = 0;

  handleError(error: any): void {
    const message = (error?.message || error?.toString() || '').toLowerCase();
    const isChunkFailed =
      message.includes('loading chunk') ||
      message.includes('failed to fetch dynamically imported module') ||
      message.includes('error loading module');

    if (isChunkFailed && typeof window !== 'undefined') {
      const now = Date.now();
      // Debounce reload: at most once every 30 seconds
      if (now - GlobalAppErrorHandler.lastReloadTime > 30000) {
        GlobalAppErrorHandler.lastReloadTime = now;
        console.warn('[AutoUpdate] Dynamic chunk failed to load due to new deployment. Auto-refreshing...', error);

        try {
          if ('caches' in window) {
            caches.keys().then(keys => keys.forEach(k => caches.delete(k))).catch(() => {});
          }
        } catch (_) {}

        const url = new URL(window.location.href);
        url.searchParams.set('_v', now.toString());
        window.location.replace(url.toString());
        return;
      }
    }

    console.error('[AppError]', error);
  }
}
